import { filter, Observable, BehaviorSubject, take } from 'rxjs';

/**
 * @module VehicleService
 */
function createVehicleService(
    apiService,
    logService,
    util
) {
    const _vehicleService = {};
    const all = 'ALL';
    const empty = 'empty';
    const invalid = 'invalid';
    const inProgress = 'inProgress';
    const vehicleSubjects = {};
    const vehicleData = {};
    const imageData = {};
    const years = {};

    function log(params) {
        params.module = 'vehicleService';
        logService.log(params);
        return params;
    }

    function sortById(a, b) {
        if (a.id < b.id) {
            return -1;
        }
        if (a.id > b.id) {
            return 1;
        }
        return 0;
    }

    function clear() {
        util.clear(years);
        util.clear(vehicleSubjects);
        util.clear(vehicleData);
        util.clear(imageData);
    }

    function processImages(imagesResponse) {
        if (imagesResponse?.config?.vehicleId && Array.isArray(imagesResponse?.data?.items)) {
            const vehicle = getSubject({ id: imagesResponse.config.vehicleId }).getValue();
            if (vehicle) {
                vehicle.images.splice(0);
                imagesResponse.data.items.forEach(imageItem => {
                    imageItem.url = apiService.vehicles.imageUrl({
                        vehicleId: vehicle.id,
                        imageId: imageItem.id
                    });
                    vehicle.images.push(imageItem);
                });
                vehicle.images.sort(sortById);
                getSubject({ id: imagesResponse.config.vehicleId}).next(vehicle);
            }
        }
    }

    function getImages(params) {
        if (params?.id) {
            if (!imageData[params.id]) {
                imageData[params.id] = true;
                apiService.vehicles.getImages({
                    vehicleId: params.id,
                    traceId: params.traceId
                }).then(
                    processImages,
                    e => {
                        log({
                            code: 'vehicleGetImageFail',
                            error: e,
                            object: params,
                            traceId: params?.traceId
                        });
                    }
                );
            }
            return;
        }
        const calls = [];
        Object.keys(vehicleSubjects).forEach(vehicleId => {
            if (vehicleId !== all && !imageData[vehicleId]) {
                imageData[vehicleId] = true;
                calls.push(apiService.vehicles.getImages({
                    vehicleId: vehicleId,
                    traceId: params?.traceId
                }).then(
                    processImages,
                    e => {
                        log({
                            code: 'vehicleGetImageFail',
                            error: e,
                            object: { id: vehicleId },
                            traceId: params?.traceId
                        });
                    }
                ));
            }
        });
        Promise.all(calls).then(
            () => getSubject().next(getSubject().getValue()),
            util.noop
        );
    }

    function getSubject(params) {
        if (params?.id) {
            vehicleSubjects[params.id] ??= new BehaviorSubject();
            return vehicleSubjects[params.id];
        }
        vehicleSubjects[all] ??= new BehaviorSubject();
        return vehicleSubjects[all];
    }

    function processVehicles(rawVehicles, requestedAll) {
        if (requestedAll) {
            // if the data was intended to have all vehicles, but some vehicles
            // have been deleted from the server, then remove the deleted vehicle
            // and corresponding data.
            const idsToRemove = Object.keys(vehicleSubjects).filter(id => {
                return !rawVehicles.find(v => v.id === id) && id !== all && id !== empty;
            });
            idsToRemove.forEach(id => {
                // observers of a deleted vehicle will receive emit undefined
                getSubject({ id: id }).next();
                delete vehicleData[id];
                delete imageData[id];
                delete vehicleSubjects[id];
            });
        }
        rawVehicles.forEach(rawVehicle => {
            let existing = getSubject({ id: rawVehicle.id }).getValue();
            if (existing) {
                util.merge(existing, rawVehicle);
            } else {
                existing = rawVehicle;
                existing.images = [];
            }
            getSubject({ id: rawVehicle.id }).next(existing);
        });
        let vehicles = getSubject().getValue();
        if (vehicles) {
            vehicles.splice(0);
        } else {
            vehicles = [];
        }
        Object.keys(vehicleSubjects).forEach(key => {
            if (key !== all) {
                vehicles.push(vehicleSubjects[key].getValue());
            }
        });
        vehicles.sort(sortById);
        getSubject().next(vehicles);
    }

    function getData(params) {
        if (params?.id) {
            // get a single vehicle if not already done and "all" not already done
            if (!vehicleData[params.id] && !vehicleData[all]) {
                vehicleData[params.id] = true;
                apiService.vehicles.getById({
                    vehicleId: params.id,
                    traceId: params.traceId
                }).then(
                    vehicleResult => {
                        processVehicles([ vehicleResult.data ]);
                        getImages(params);
                    },
                    e => {
                        log({
                            code: 'getVehicleFail',
                            error: e,
                            object: params
                        });
                    }
                );
            }
            return;
        }
        if (!vehicleData[all]) {
            // get all vehicles if not already done
            vehicleData[all] = true;
            apiService.vehicles.get({
                traceId: params.traceId
            }).then(
                vehiclesResult => {
                    processVehicles(vehiclesResult.data.items, true);
                    getImages(params);
                },
                e => {
                    log({
                        code: 'getVehiclesFail',
                        error: e,
                        object: params
                    });
                }
            );
        }
    }

    function applyRefreshParam(params) {
        if (params?.refresh) {
            if (params.id) {
                delete vehicleData[params.id];
                delete imageData[params.id];
            } else {
                util.clear(vehicleData);
                util.clear(imageData);
            }
        }
    }

    function afterRemove(removeResult, params) {
        if (removeResult.config?.vehicleId) {
            const id = removeResult.config.vehicleId;
            const vehicles = getSubject().getValue();
            const toRemove = vehicles.find(util.byId, { id: removeResult.config.vehicleId });
            if (toRemove) {
                const index = vehicles.indexOf(toRemove);
                if (index > -1) {
                    vehicles.splice(index, 1);
                    delete vehicleData[id];
                    delete imageData[id];
                    getSubject().next(vehicles);
                    getSubject({ id: id }).next();
                    delete vehicleSubjects[id];
                }
            }
        }
        log({
            code: 'removeVehicle',
            type: 'info',
            object: params,
            traceId: params.traceId
        });
    }

    function assignImageToNewAfterCreateVehicle(newVehicle, subscriber, params) {
        if (newVehicle) {
            _vehicleService.assignImageToVehicle({
                vehicleId: params.vehicleId,
                imageId: params.imageId,
                newVehicleId: newVehicle.id,
                traceId: params.traceId
            }).subscribe({
                next() {
                    let i = 0;
                    // on the third emit, new image data will be ready
                    _vehicleService.get({
                        refresh: true,
                        traceId: params.traceId
                    }).pipe(take(3)).subscribe({
                        next() {
                            i += 1;
                            if (i === 3) {
                                log({
                                    code: 'assignToNewVehicle',
                                    type: 'info',
                                    obejct: params,
                                    traceId: params.traceId
                                });
                                subscriber.next();
                                subscriber.complete();
                            }
                        },
                        error: e => subscriber.error(
                            log({
                                code: 'assignToNewNoVehicle',
                                obejct: params,
                                error: e,
                                traceId: e?.traceId
                            })
                        )
                    });
                },
                error: e => subscriber.error(
                    log({
                        code: 'assignToNewVehicleAssignFail',
                        obejct: params,
                        error: e,
                        traceId: e?.traceId
                    })
                )
            });
        } else {
            subscriber.error(
                log({
                    code: 'newVehicleInvalid',
                    obejct: newVehicle,
                    traceId: params.traceId
                })
            );
        }
    }

    // ****************************
    // public members
    // ****************************

    /**
     * @property {String} invalid A text constant
     * @name invalid
     */
    _vehicleService.invalid = invalid;

    /**
     * @property {String} empty A text constant
     * @name empty
     */
    _vehicleService.empty = empty;

    /**
     * @description Clients can observe one vehicle or the collection of all vehicles.
     *     The returned observables will emit vehicle objects without image data at first
     *     for fast UI rendering. After images are automatically loaded, observables emit again,
     *     this time containing image data. Nothing else needs to be done to load images.
     * @param {object|undefined} [params] Parameters object, or undefined to return all vehicles.
     *     If param "id" is defined, the Observable will emit undefined when the vehicle is removed.
     * @param {String} [params.id] The id of the requested vehicle, or undefined to get all vehicles at once
     * @param {String} [params.refresh] true to refresh data, false to use existing data if any
     * @param {String} [params.traceId]
     * @returns {Observable<Vehicle>|Observable<Array<Vehicle>>} emits a vehicle or vehicles depending on id param
     * @name get
     * @method
     */
    _vehicleService.get = function(params) {
        const internalParams = util.withTraceId(params);
        applyRefreshParam(internalParams);
        const subject = getSubject(internalParams);
        getData(internalParams);
        return subject.asObservable();
    };

    /**
     * @description Updates vehicle data but does not affect related images
     * @param {Object} params
     * @param {Object} params.vehicle A vehicle to add or update
     * @param {String} [params.traceId]
     * @returns {Observable} Emits saved vehicle when the save is successful.
     *     To get the saved data, observe vehicleService.get
     * @name save
     * @method
     */
    _vehicleService.save = function(params) {
        return new Observable(subscriber => {
            if (!params || !params?.vehicle) {
                subscriber.error(
                    log({
                        code: 'saveVehicleParams',
                        object: params,
                        traceId: params?.traceId
                    })
                );
                return;
            }
            const internalParams = util.withTraceId(params);
            if (internalParams.vehicle.id) {
                // PUT for existing
                apiService.vehicles.put(internalParams).then(
                    result => {
                        processVehicles([ result.data ]);
                        subscriber.next(result.data);
                        subscriber.complete();
                    },
                    e => subscriber.error(
                        log({
                            code: 'saveExistingVehicle',
                            object: internalParams,
                            error: e,
                            traceId: e?.traceId
                        })
                    )
                );
            } else {
                // POST for new
                apiService.vehicles.post(internalParams).then(
                    result => {
                        processVehicles([ result.data ]);
                        subscriber.next(result.data);
                        subscriber.complete();
                    },
                    e => subscriber.error(
                        log({
                            code: 'saveNewVehicle',
                            object: internalParams,
                            error: e,
                            traceId: e?.traceId
                        })
                    )
                );
            }
        });
    };

    /**
     * @description Removes a vehicle
     * @param {Object} params
     * @param {String} params.vehicleId The vehicle to remove
     * @param {String} [params.traceId]
     * @returns {Observable<string>} Emits the ID of the removed vehicle
     * @name remove
     * @method
     */
    _vehicleService.remove = function(params) {
        return new Observable(subscriber => {
            if (!params?.vehicleId || params.vehicleId === empty) {
                subscriber.error(
                    log({
                        code: 'removeVehicleParams',
                        object: params,
                        traceId: params?.traceId
                    })
                );
                return;
            }
            const internalParams = util.withTraceId(params);
            apiService.vehicles.remove(internalParams).then(
                removeResult => {
                    afterRemove(removeResult, internalParams);
                    subscriber.next(internalParams.vehicleId);
                    subscriber.complete();
                },
                e => subscriber.error(
                    log({
                        code: 'removeVehicle',
                        object: { vehicleId: internalParams.vehicleId },
                        error: e,
                        traceId: e?.traceId
                    })
                )
            );
        });
    };

    /**
     * @description Assigns an image from one vehicle to another,
     *     or to special values _vehicleService.empty or _vehicleService.invalid
     *     to specify the empty space or that the image is invalid (pulling in or out)
     *     and should not be assigned to any vehicle.
     * @param {Object} params Specification object
     * @param {String} params.vehicleId Old vehicleId for the image
     * @param {String} params.imageId ID of the image
     * @param {String} params.newVehicleId New vehicleId to assign the image to.
     *     Use the special value "empty" if the spot was empty
     *     or "invalid" if the vehicle was pulling in or out.
     * @param {String} [params.traceId]
     * @returns {Observable} Emits when reassignment is complete
     * @name assignImageToVehicle
     * @method
     */
    _vehicleService.assignImageToVehicle = function(params) {
        return new Observable(subscriber => {
            if (!params?.vehicleId || !params?.imageId || !params?.newVehicleId) {
                subscriber.error(
                    log({
                        code: 'assignImageBadParams',
                        object: params,
                        traceId: params?.traceId
                    })
                );
                return;
            }
            const internalParams = util.withTraceId(params);
            apiService.vehicles.reassign(internalParams).then(
                () => {
                    _vehicleService.get({ refresh: true }).subscribe({
                        next() {
                            subscriber.next();
                            subscriber.complete();
                        },
                        error: e => subscriber.error(
                            log({
                                code: 'assignImageGetVehicleFail',
                                error: e,
                                traceId: e?.traceId
                            })
                        )
                    });
                },
                e => subscriber.error(
                    log({
                        code: 'assignImageApiFail',
                        object: params,
                        error: e,
                        traceId: e?.traceId
                    })
                )
            );
        });
    };

    /**
     * @description Creates a new vehicle and assigns an image to it. The optional params.newVehicle
     *     specifies properties for the new vehicle.
     * @param {Object} params Specification object
     * @param {String} params.vehicleId vehicleId for the image
     * @param {String} params.imageId ID of the image
     * @param {String} [params.newVehicle] an optional new vehicle to create and assign the image to.
     * @param {String} [params.traceId]
     *     If undefined, a default name for the vehicle will be created
     * @returns {Observable} Emits when reassignment is complete
     * @name assignImageToNew
     * @method
     */
    _vehicleService.assignImageToNew = function(params) {
        return new Observable(subscriber => {
            // the new vehicle, if there is one, should not have an id
            if (!params?.vehicleId || !params?.imageId || params?.newVehicle?.id) {
                subscriber.error(
                    log({
                        code: 'assignImageToNewParams',
                        object: params,
                        traceId: params?.traceId
                    })
                );
                return;
            }
            const internalParams = util.withTraceId(params);
            const toCreate = util.merge({}, (params.newVehicle ?? {}));
            toCreate.name ??= 'Newly Created Vehicle';
            _vehicleService.save({ vehicle: toCreate, traceId: internalParams.traceId}).subscribe({
                next: newVehicle => {
                    assignImageToNewAfterCreateVehicle(newVehicle, subscriber, internalParams);
                },
                error: e => subscriber.error(
                    log({
                        code: 'assignToNewVehicleSaveNew',
                        obejct: params,
                        error: e,
                        traceId: internalParams
                    })
                )
            });
        });
    };

    /**
     * @param {Object} params
     * @param {Number} params.year
     * @param {String} [params.traceId]
     * @returns {Observable<Array<VehicleMake>>} Emits an array of vehicle makes
     *     for a given model year
     * @name getMakes
     * @method
     */
    _vehicleService.getMakes = function(params) {
        if (!params?.year) {
            return new Observable(subscriber => subscriber.error(
                log({
                    code: 'loginBadParams',
                    traceId: params?.traceId
                })
            ));
        }
        return new Observable(subscriber => {
            const paramsWithTraceId = util.withTraceId(params);
            if (!years[params.year]) {
                years[params.year] = inProgress;
                apiService.vehicles.getMakes(paramsWithTraceId).then(
                    result => {
                        years[params.year] = result.data.items;
                        subscriber.next(years[params.year]);
                        subscriber.complete();
                    },
                    e => subscriber.error(
                        log({
                            code: 'getMakesFailed',
                            error: e,
                            object: params,
                            traceId: paramsWithTraceId.traceId
                        })
                    )
                );
                return;
            }
            if (years[params.year] !== inProgress) {
                subscriber.next(years[params.year]);
                subscriber.complete();
            }
        });
    };

    /**
     * @param {Object} params
     * @param {Number} params.year vehicle model year
     * @param {String} params.make vehicle make make
     * @param {String} [params.traceId]
     * @returns {Observable<Array<vehicleModel>>} Emits an array of vehicle models
     *     for the given year and make
     * @name getModels
     * @method
     */
    _vehicleService.getModels = function(params) {
        if (!params?.year || !params.make) {
            return new Observable(subscriber => subscriber.error(
                log({
                    code: 'loginBadParams',
                    traceId: params?.traceId
                })
            ));
        }
        return new Observable(subscriber => {
            const paramsWithTraceId = util.withTraceId(params);
            _vehicleService.getMakes(paramsWithTraceId).subscribe({
                next: makes => {
                    const foundMake = makes.find(x => x.name === paramsWithTraceId.make);
                    if (foundMake) {
                        subscriber.next(foundMake.models);
                        subscriber.complete();
                        return;
                    }
                    subscriber.error(
                        log({
                            code: 'noModelsFound',
                            object: params,
                            traceId: paramsWithTraceId.traceId
                        })
                    );
                },
                error: e => subscriber.error(
                    log({
                        code: 'getModelsFailed',
                        error: e,
                        object: params,
                        traceId: paramsWithTraceId.traceId
                    })
                )
            });
        });
    };

    /**
     * @param {Object} params
     * @param {Number} params.year vehicle model year
     * @param {String} params.make vehicle make make
     * @param {String} params.model vehicle model
     * @param {String} [params.traceId]
     * @returns {Observable<vehicleModel>} Emits a vehicle model object
     *     for the given year, make and model name
     * @name getModel
     * @method
     */
    _vehicleService.getModel = function(params) {
        if (!params?.year || !params.make || !params.model) {
            return new Observable(subscriber => subscriber.error(
                log({
                    code: 'loginBadParams',
                    traceId: params?.traceId
                })
            ));
        }
        return new Observable(subscriber => {
            const paramsWithTraceId = util.withTraceId(params);
            _vehicleService.getModels(paramsWithTraceId).subscribe({
                next: models => {
                    const foundModel = models.find(x => x.name === paramsWithTraceId?.model);
                    if (foundModel) {
                        subscriber.next(foundModel);
                        subscriber.complete();
                        return;
                    }
                    subscriber.error(
                        log({
                            code: 'getModelNotFound',
                            object: params,
                            traceId: paramsWithTraceId.traceId
                        })
                    );
                },
                error: e => subscriber.error(
                    log({
                        code: 'getModelFailed',
                        error: e,
                        object: params,
                        traceId: paramsWithTraceId.traceId
                    })
                )
            });
        });
    };

    /**
     * @param {Object} [params]
     * @param {String} [params.deviceId] if this optional param is included, then only verify for this device,
     *     otherwise verify for all devices
     * @param {String} [params.traceId]
     * @returns {Observable}
     * @name verify
     * @method
     */
    _vehicleService.verify = function(params) {
        return new Observable(subscriber => {
            const internalParams = util.withTraceId(params);
            if (internalParams.deviceId) {
                apiService.vehicles.verifyDevice(internalParams).then(
                    () => {
                        subscriber.next();
                        subscriber.complete();
                    },
                    e => subscriber.error(
                        log({
                            code: 'vehicleVerifyDeviceFail',
                            error: e,
                            deviceId: params?.deviceId,
                            traceId: internalParams.traceId
                        })
                    )
                );
            } else {
                apiService.vehicles.verifyAll(internalParams).then(
                    () => {
                        subscriber.next();
                        subscriber.complete();
                    },
                    e => subscriber.error(
                        log({
                            code: 'vehicleVerifyAllFail',
                            error: e,
                            traceId: internalParams.traceId
                        })
                    )
                );
            }
        });
    };

    // Any time loggedIn emits false, run clear
    apiService.loggedIn().pipe(filter(l => !l)).subscribe(() => clear());

    return Object.freeze(_vehicleService);
}

export default createVehicleService;
